import { useEffect, useRef } from 'react';

import { useGetEnterpriseQuery } from '../api/enterpriseApi';
import { ANALYTICS_PROPERTIES } from '../constants/analytics';
import { Analytics } from '../services/analytics';

import { useMe } from './useMe';

/**
 * Wires PostHog identity + tenant context into the platform.
 *
 * On user/enterprise resolve: calls Analytics.identifyWithTenant so every
 * subsequent track event carries tenant_id, tenant_name, and user_id.
 * On sign-out (user becomes undefined): calls Analytics.reset.
 *
 * The enterprise query is gated on `user.id` so we don't fire `/enterprise/hd`
 * (and trigger refresh-token churn) on public routes like /login or /signup.
 */
export function useIdentifyForAnalytics(): void {
	const { data: user } = useMe();
	const { data: enterpriseData } = useGetEnterpriseQuery(undefined, {
		skip: !user?.id,
	});
	const lastIdentityKey = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!user?.id) {
			if (lastIdentityKey.current) {
				Analytics.reset();
				lastIdentityKey.current = undefined;
			}
			return;
		}

		const tenantId = enterpriseData?.id ?? user.enterprise_id;
		const tenantName = enterpriseData?.name;

		// Re-identify whenever any identity dimension changes — including
		// `tenantName`, which arrives later than `user.id` on initial load and
		// would otherwise be missed by a user-id-only dedupe guard.
		const identityKey = [
			user.id,
			tenantId ?? '',
			tenantName ?? '',
			user.email ?? '',
			user.role ?? '',
			user.type ?? '',
		].join('|');

		if (lastIdentityKey.current === identityKey) {
			return;
		}

		Analytics.identifyWithTenant(
			user.id,
			{ id: tenantId, name: tenantName },
			{
				email: user.email,
				[ANALYTICS_PROPERTIES.USER_ROLE]: user.role,
				[ANALYTICS_PROPERTIES.USER_TYPE]: user.type,
			},
		);

		lastIdentityKey.current = identityKey;
	}, [
		user?.id,
		user?.email,
		user?.role,
		user?.type,
		user?.enterprise_id,
		enterpriseData?.id,
		enterpriseData?.name,
	]);
}
